import { createSlice } from '@reduxjs/toolkit';
import { InitialGlobalCatalogueProps } from '../../../interfaces/IGlobalCatalogue';
import { DEFAULT_PAGE_SIZE } from '../../../constants/Constant';

const initialPaginationState: InitialGlobalCatalogueProps['filters']['pagination'] =
  {
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: undefined,
  };

const initialSegmentFilter: InitialGlobalCatalogueProps['filters']['segment'] =
  {
    segmentId: '',
  };

const initialState: InitialGlobalCatalogueProps = {
  collectionSelected: '',
  // GOrganisation.orgId (catalogue tenant) — distinct from activeOrganisationReducer.organisationId (client file org)
  activeGOrganisation: '',
  collectionOptions: {
    actionModeId: [],
    activeUnit: [],
    agSectorId: [],
    attrUnit: [],
    chemicalGroupId: [],
    co2eUnit: [],
    concentrationUnit: [],
    crop: [],
    genusSpeciesId: [],
    mainEndUseId: [],
    manufacturerId: [],
    moaGroupId: [],
    operationUnit: [],
    packSizeUnit: [],
    parameterId: [],
    productGradeId: [],
    productGroups: [],
    productUnit: [],
    physicalStateId: [],
    rainfastUnit: [],
    rateUnit: [],
    reentryUnit: [],
    segments: [],
    speciesTypeId: [],
    supplierId: [],
    techStackId: [],
  },
  gcTableData: [],
  loading: false,
  filters: {
    pagination: initialPaginationState,
    segment: initialSegmentFilter,
    sorter: {},
  },
  searchState: {
    searchText: '',
    filterManufacturers: [],
    isSearchApplied: false,
  },
};

const globalCatalogueSlice = createSlice({
  name: 'globalCatalogueSlice',
  initialState,
  reducers: {
    setCollectionSelected: (state, action) => {
      return { ...state, collectionSelected: action.payload };
    },
    setActiveGOrganisation: (state, action) => {
      return { ...state, activeGOrganisation: action.payload };
    },
    setGCTableData: (state, action) => {
      return { ...state, gcTableData: action.payload };
    },
    resetGCState: () => {
      return {
        ...initialState,
        filters: {
          ...initialState.filters,
          pagination: { ...initialState.filters.pagination },
          segment: { ...initialState.filters.segment },
          sorter: { ...initialState.filters.sorter },
        },
      };
    },
    setLoaderState: (state, action) => {
      return { ...state, loading: action.payload };
    },
    setAllActiveGSeedSpeciesCrops: (state, action) => {
      return {
        ...state,
        collectionOptions: {
          ...state.collectionOptions,
          crop: action.payload,
        },
      };
    },
    setAllActiveGSegments: (state, action) => {
      return {
        ...state,
        collectionOptions: {
          ...state.collectionOptions,
          segments: action.payload,
        },
      };
    },
    setAllActiveGParameters: (state, action) => {
      return {
        ...state,
        collectionOptions: {
          ...state.collectionOptions,
          parameterId: action.payload,
        },
      };
    },
    setAllActiveGProductGrades: (state, action) => {
      return {
        ...state,
        collectionOptions: {
          ...state.collectionOptions,
          productGradeId: action.payload,
        },
      };
    },
    setAllActiveGProductGroups: (state, action) => {
      return {
        ...state,
        collectionOptions: {
          ...state.collectionOptions,
          productGroups: action.payload,
        },
      };
    },
    setAllActiveGPhysicalState: (state, action) => {
      return {
        ...state,
        collectionOptions: {
          ...state.collectionOptions,
          physicalStateId: action.payload,
        },
      };
    },
    setAllActiveGChemicalGroup: (state, action) => {
      return {
        ...state,
        collectionOptions: {
          ...state.collectionOptions,
          chemicalGroupId: action.payload,
        },
      };
    },
    setAllActiveGGenusSpecies: (state, action) => {
      return {
        ...state,
        collectionOptions: {
          ...state.collectionOptions,
          genusSpeciesId: action.payload,
        },
      };
    },
    setAllActiveGActionModes: (state, action) => {
      return {
        ...state,
        collectionOptions: {
          ...state.collectionOptions,
          actionModeId: action.payload,
        },
      };
    },
    setAllActiveGAgSectors: (state, action) => {
      return {
        ...state,
        collectionOptions: {
          ...state.collectionOptions,
          agSectorId: action.payload,
        },
      };
    },
    setAllActiveGMainEndUse: (state, action) => {
      return {
        ...state,
        collectionOptions: {
          ...state.collectionOptions,
          mainEndUseId: action.payload,
        },
      };
    },
    setAllActiveGManufacturers: (state, action) => {
      return {
        ...state,
        collectionOptions: {
          ...state.collectionOptions,
          manufacturerId: action.payload,
        },
      };
    },
    setAllActiveGMoaGroups: (state, action) => {
      return {
        ...state,
        collectionOptions: {
          ...state.collectionOptions,
          moaGroupId: action.payload,
        },
      };
    },
    setAllActiveGSpeciesTypes: (state, action) => {
      return {
        ...state,
        collectionOptions: {
          ...state.collectionOptions,
          speciesTypeId: action.payload,
        },
      };
    },
    setAllActiveGSupplierNames: (state, action) => {
      return {
        ...state,
        collectionOptions: {
          ...state.collectionOptions,
          supplierId: action.payload,
        },
      };
    },
    setAllActiveGTechStack: (state, action) => {
      return {
        ...state,
        collectionOptions: {
          ...state.collectionOptions,
          techStackId: action.payload,
        },
      };
    },
    setGOperationUnits: (state, action) => {
      return {
        ...state,
        collectionOptions: {
          ...state.collectionOptions,
          operationUnit: action.payload,
        },
      };
    },
    setGCo2eUnits: (state, action) => {
      return {
        ...state,
        collectionOptions: {
          ...state.collectionOptions,
          co2eUnit: action.payload,
        },
      };
    },
    setGConcentrationUnits: (state, action) => {
      return {
        ...state,
        collectionOptions: {
          ...state.collectionOptions,
          concentrationUnit: [
            ...state.collectionOptions.concentrationUnit,
            ...action.payload,
          ],
        },
      };
    },
    resetGConcentrationUnits: (state) => {
      return {
        ...state,
        collectionOptions: {
          ...state.collectionOptions,
          concentrationUnit: [],
        },
      };
    },
    setGRateUnits: (state, action) => {
      return {
        ...state,
        collectionOptions: {
          ...state.collectionOptions,
          rateUnit: [...state.collectionOptions.rateUnit, ...action.payload],
        },
      };
    },
    resetGRateUnits: (state) => {
      return {
        ...state,
        collectionOptions: {
          ...state.collectionOptions,
          rateUnit: [],
        },
      };
    },
    setGActiveUnits: (state, action) => {
      return {
        ...state,
        collectionOptions: {
          ...state.collectionOptions,
          activeUnit: [
            ...state.collectionOptions.activeUnit,
            ...action.payload,
          ],
        },
      };
    },
    resetGActiveUnits: (state) => {
      return {
        ...state,
        collectionOptions: {
          ...state.collectionOptions,
          activeUnit: [],
        },
      };
    },
    setGPackSizeUnits: (state, action) => {
      return {
        ...state,
        collectionOptions: {
          ...state.collectionOptions,
          packSizeUnit: [
            ...state.collectionOptions.packSizeUnit,
            ...action.payload,
          ],
        },
      };
    },
    resetGPackSizeUnits: (state) => {
      return {
        ...state,
        collectionOptions: {
          ...state.collectionOptions,
          packSizeUnit: [],
        },
      };
    },
    setGReentryUnits: (state, action) => {
      return {
        ...state,
        collectionOptions: {
          ...state.collectionOptions,
          reentryUnit: action.payload,
        },
      };
    },
    setGRainfastUnits: (state, action) => {
      return {
        ...state,
        collectionOptions: {
          ...state.collectionOptions,
          rainfastUnit: action.payload,
        },
      };
    },
    setGProductUnits: (state, action) => {
      return {
        ...state,
        collectionOptions: {
          ...state.collectionOptions,
          productUnit: [
            ...state.collectionOptions.productUnit,
            ...action.payload,
          ],
        },
      };
    },
    resetGProductUnits: (state) => {
      return {
        ...state,
        collectionOptions: {
          ...state.collectionOptions,
          productUnit: [],
        },
      };
    },
    setGAttrUnits: (state, action) => {
      return {
        ...state,
        collectionOptions: {
          ...state.collectionOptions,
          attrUnit: [...state.collectionOptions.attrUnit, ...action.payload],
        },
      };
    },
    resetGAttrUnits: (state) => {
      return {
        ...state,
        collectionOptions: {
          ...state.collectionOptions,
          attrUnit: [],
        },
      };
    },
    setPagination: (state, action) => {
      return {
        ...state,
        filters: {
          ...state.filters,
          pagination: {
            ...state.filters.pagination,
            current: action.payload.current,
            pageSize: action.payload.pageSize,
          },
        },
      };
    },
    resetPagination: (state) => {
      return {
        ...state,
        filters: {
          ...state.filters,
          pagination: initialPaginationState,
        },
      };
    },
    setTotalData: (state, action) => {
      if (typeof action.payload === 'number') {
        return {
          ...state,
          filters: {
            ...state.filters,
            pagination: {
              ...state.filters.pagination,
              total: action.payload,
            },
          },
        };
      } else {
        return {
          ...state,
          filters: {
            ...state.filters,
            pagination: {
              ...state.filters.pagination,
              total: action.payload?.estimatedTotal,
              hasMore: action.payload?.hasMore,
              pageIndex: action.payload?.pageIndex,
            },
          },
        };
      }
    },
    setSegmentFilter: (state, action) => {
      return {
        ...state,
        filters: {
          ...state.filters,
          segment: {
            segmentId: action.payload.segmentId,
          },
        },
      };
    },
    resetSegmentFilter: (state) => {
      return {
        ...state,
        filters: {
          ...state.filters,
          segment: {
            segmentId: '',
          },
        },
      };
    },
    setSorting: (state, action) => {
      return {
        ...state,
        filters: {
          ...state.filters,
          sorter: {
            ...state.filters.sorter,
            [action.payload.collection]: {
              dataIndex: action.payload.dataIndex,
              order: action.payload.order,
            },
          },
        },
      };
    },
    setSearchState: (state, action) => {
      return {
        ...state,
        searchState: {
          ...state.searchState,
          ...action.payload,
        },
      };
    },
    resetSearchState: (state) => {
      return {
        ...state,
        searchState: {
          searchText: '',
          filterManufacturers: [],
          isSearchApplied: false,
        },
      };
    },
  },
});

export const {
  setCollectionSelected,
  setActiveGOrganisation,
  setAllActiveGSegments,
  setGCTableData,
  resetGCState,
  setLoaderState,
  setAllActiveGSeedSpeciesCrops,
  setAllActiveGParameters,
  setAllActiveGProductGrades,
  setAllActiveGProductGroups,
  setAllActiveGPhysicalState,
  setAllActiveGChemicalGroup,
  setAllActiveGGenusSpecies,
  setAllActiveGActionModes,
  setAllActiveGAgSectors,
  setAllActiveGMainEndUse,
  setAllActiveGManufacturers,
  setAllActiveGMoaGroups,
  setAllActiveGSpeciesTypes,
  setAllActiveGSupplierNames,
  setAllActiveGTechStack,
  setGOperationUnits,
  setGCo2eUnits,
  setGConcentrationUnits,
  setGRateUnits,
  resetGRateUnits,
  setGActiveUnits,
  resetGActiveUnits,
  setGPackSizeUnits,
  resetGPackSizeUnits,
  resetGConcentrationUnits,
  setGReentryUnits,
  setGRainfastUnits,
  setGProductUnits,
  setGAttrUnits,
  resetGProductUnits,
  resetGAttrUnits,
  setPagination,
  resetPagination,
  setTotalData,
  setSegmentFilter,
  resetSegmentFilter,
  setSorting,
  setSearchState,
  resetSearchState,
} = globalCatalogueSlice.actions;
export default globalCatalogueSlice.reducer;
